import { activateCombatant, deactivateCombatant } from "./combat.mjs";
import { addCurrentActivations, addMaxActivations, getDisposition } from "./combatant.mjs";
/** @import {ContextMenuEntry} from "../client-esm/applications/ui/context.mjs" */

const MODULE = "lancer-initiative";

/**
 * @this {InstanceType<foundry["applications"]["sidebar"]["tabs"]["CombatTracker"]>}
 * @param {MouseEvent} ev
 * @param {HTMLButtonElement} target
 */
export async function activateCombatantTurn(ev, target) {
  ev.stopPropagation();
  const { combatantId } = target.closest("[data-combatant-id]").dataset;
  activateCombatant(this.viewed, combatantId);
}
/**
 * @this {InstanceType<foundry["applications"]["sidebar"]["tabs"]["CombatTracker"]>}
 * @param {MouseEvent} ev
 * @param {HTMLButtonElement} target
 */
export async function deactivateCombatantTurn(ev, target) {
  ev.stopPropagation();
  const { combatantId } = target.closest("[data-combatant-id]").dataset;
  deactivateCombatant(this.viewed, combatantId);
}
/** @this {InstanceType<foundry["applications"]["sidebar"]["tabs"]["CombatTracker"]>} */
export async function resetAll() {
  for (const c of this.viewed.combatants) {
    const activations =
      this.viewed.round > 0 && !(this.viewed.settings.skipDefeated && c.isDefeated)
        ? c.getFlag(MODULE, "activations.max") ?? 1
        : 0;
    c.updateSource({ initiative: null, "flags.lancer-initiative.activations.value": activations });
  }
  this.viewed.update(
    { turn: null, combatants: this.viewed.combatants.toObject() },
    { diff: false }
  );
}

/**
 * Intercepts the data being sent to the combat tracker window and
 * optionally sorts the the turn data that gets displayed. This allows the
 * units that have already gone to be moved to the bottom without the risk of
 * updateCombat events being eaten.
 * @this {InstanceType<foundry["applications"]["sidebar"]["tabs"]["CombatTracker"]>}
 * @param {foundry["applications"]["sidebar"]["tabs"]["CombatTracker"]["prototype"]["_prepareTrackerContext"]} wrapped
 * @param {Parameters<wrapped>[0]} ctx
 * @param {Parameters<wrapped>[1]} opts
 */
export async function prepareTrackerContext(wrapped, ctx, opts) {
  const config = {
    sort: game.settings.get(MODULE, "combat-tracker-sort"),
    enable_initiative: game.settings.get(MODULE, "combat-tracker-enable-initiative"),
  };
  const appearance = game.settings.get(MODULE, "combat-tracker-appearance");
  await wrapped(ctx, opts);
  const sort = config.sort ?? true;
  const disp = { [-2]: "secret", [-1]: "enemy", [0]: "neutral", [1]: "friendly", [2]: "player" };
  ctx.turns = ctx.turns?.map(t => {
    /** @type {LancerCombatant|undefined} */
    const combatant = this.viewed.getEmbeddedDocument("Combatant", t.id);
    const activate = { icon: appearance.icon, action: "activateCombatantTurn" };
    const buttons = [
      ...Array(combatant?.getFlag(MODULE, "activations.value") ?? 0).fill({
        ...activate,
      }),
    ];
    if (combatant === this.viewed.combatant)
      buttons.push({ icon: appearance.deactivate, action: "deactivateCombatantTurn" });
    const disposition = getDisposition(combatant);
    return {
      ...t,
      css: t.css + " " + disp[disposition ?? -2],
      buttons,
      pending: combatant?.getFlag(MODULE, "activations.value") ?? 0,
    };
  });
  if (sort && ctx.turns) {
    ctx.turns.sort(function (a, b) {
      const aa = a.css.indexOf("active") !== -1 ? 1 : 0;
      const ba = b.css.indexOf("active") !== -1 ? 1 : 0;
      if (ba - aa !== 0) return ba - aa;
      const ad = a.pending === 0 ? 1 : 0;
      const bd = b.pending === 0 ? 1 : 0;
      return ad - bd;
    });
  }
  ctx.enable_initiative = config.enable_initiative ?? false;
}

/**
 * @this {InstanceType<foundry["applications"]["sidebar"]["tabs"]["CombatTracker"]>}
 * @returns {ContextMenuEntry[]}
 */
export function getEntryContextOptions(wrapped) {
  const getCombatant = li => this.viewed.combatants.get(li.dataset.combatantId);
  /** @type {ContextMenuEntry[]} */
  const m = [
    {
      name: "LANCERINITIATIVE.AddActivation",
      icon: '<i class="fas fa-plus"></i>',
      callback: li => addMaxActivations(getCombatant(li), 1),
    },
    {
      name: "LANCERINITIATIVE.RemoveActivation",
      icon: '<i class="fas fa-minus"></i>',
      callback: li => addMaxActivations(getCombatant(li), -1),
    },
    {
      name: "LANCERINITIATIVE.UndoActivation",
      icon: '<i class="fas fa-undo"></i>',
      callback: li =>
        deactivateCombatant(this.viewed, li.dataset.combatantId).then(() =>
          addCurrentActivations(getCombatant(li), 1)
        ),
    },
  ];
  m.push(...wrapped());
  m.find(i => i.name === "COMBAT.CombatantReroll").condition = () =>
    game.settings.get(MODULE, "combat-tracker-enable-initiative");
  return m;
}

/**
 * @param {InstanceType<foundry["applications"]["sidebar"]["tabs"]["CombatTracker"]>} app
 * @param {HTMLElement} html
 */
export function renderCombatTracker(app, html) {
  if (!game.settings.get(MODULE, "combat-tracker-enable-initiative")) {
    html.querySelector("header .encounter-controls [data-action=rollAll]").disabled = true;
    html.querySelector("header .encounter-controls [data-action=rollNPC]").disabled = true;
  }
}
